// MARK: NPM Modules
import React, { useState } from "react";
import styled from "styled-components";
// MARK: Redux
import { RootState } from "reduxFunctionality/index";
import { useSelector } from "react-redux";
// MARK: Types
import ReduxStyleguide from "types/redux/styleguide";
import NavigationItemPage from "types/globals/navigationBar/navigationItemPage";
// MARK: Components
import Link from "components/common/links/standard";
import Typography from "components/common/typography";
// MARK: Functionality
import URI from "utils/common/uri";
import determineActiveLink from "../shared/determineActiveLink";
import CSSStyles from "enums/cssStyles";

// MARK: Styled Components

interface SecondaryMenuItemProps {
  marginbottom: number;
  backgroundcolor: string;
  highlightColor: string;
}

const Container = styled.li<SecondaryMenuItemProps>`
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 48px;
  background-color: ${({ backgroundcolor }) => backgroundcolor};
  margin-bottom: ${({ marginbottom }) => marginbottom}px;
  :hover {
    background-color: ${({ highlightColor }) => highlightColor};
  }
`;

interface PaddedLinkProps {
  padding: number;
}

const PaddedLink = styled(Link)<PaddedLinkProps>`
  padding: ${({ padding }) => padding}px;
`;

// MARK: React Component
// Props
interface Props {
  item: NavigationItemPage;
  activeRoute: string | undefined;
}
// Styleguide & Datapoints
const getDatapoints = (styleguide: ReduxStyleguide) => {
  const { colors } = styleguide;
  const { primary, background } = colors;
  return {
    color: primary,
    background: background,
  };
};
// Component
const SecondaryMenuItem = ({ item, activeRoute }: Props) => {
  // MARK: React Redux Variables
  const styleguide: ReduxStyleguide = useSelector(
    (state: RootState) => state.styleguide
  );
  // MARK: State Variables
  const [isHoveringOver, setIsHoveringOver] = useState<boolean>(false);
  // MARK: Variables
  const { gutter } = styleguide;
  const { color, background } = getDatapoints(styleguide);
  const active = determineActiveLink(item, activeRoute);
  const { title, uri } = item;
  // MARK: Functionality

  const generateIdForSecondaryMenuItem = (header: string) => {
    return "secondary-navigation-bar-item-" + header.toLowerCase();
  };

  // MARK: Render
  return (
    <Container
      key={generateIdForSecondaryMenuItem(title)}
      itemProp="name"
      role="menuitem"
      marginbottom={gutter}
      highlightColor={color}
      backgroundcolor={background}
      onMouseEnter={() => {
        setIsHoveringOver(true);
      }}
      onMouseLeave={() => {
        setIsHoveringOver(false);
      }}
    >
      <PaddedLink
        title={title}
        to={uri === "__home__" ? "/" : URI(uri)}
        padding={gutter}
      >
        <Typography
          className={CSSStyles.navigation}
          type={"span"}
          color={isHoveringOver ? background : color}
          hoverColor={background}
          textDecoration={active ? "underline" : ""}
          textAlign={"center"}
        >
          {title.toLowerCase()}
        </Typography>
      </PaddedLink>
    </Container>
  );
};
export default SecondaryMenuItem;
